import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Save, Image, Calendar, FileText, MessageSquare, Users, MapPin } from 'lucide-react'
import { adminApi } from '../lib/api'
import { transformTrips, transformWaybookAccounts } from '../lib/transformers'
import type { Trip, WaybookAccount } from '../types'
import GeneralTab from '../components/trip-tabs/GeneralTab'
import EventsTab from '../components/trip-tabs/EventsTab'
import PostsTab from '../components/trip-tabs/PostsTab'
import FilesTab from '../components/trip-tabs/FilesTab'
import NotesTab from '../components/trip-tabs/NotesTab'
import ParticipantsTab from '../components/trip-tabs/ParticipantsTab'
import LocationTab from '../components/trip-tabs/LocationTab'
import { useToast } from '../components/ui/ToastProvider'

type TabType = 'general' | 'events' | 'posts' | 'files' | 'notes' | 'participants' | 'location'

export default function TripDetailPage() {
  const { showToast } = useToast()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isNewTrip = id === 'new'

  const [activeTab, setActiveTab] = useState<TabType>('general')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [trip, setTrip] = useState<Trip | null>(null)
  const [waybookAccounts, setWaybookAccounts] = useState<WaybookAccount[]>([])

  // Preloaded data from /trips/:id/full endpoint
  const [preloadedEvents, setPreloadedEvents] = useState<any[]>([])
  const [preloadedFiles, setPreloadedFiles] = useState<any[]>([])
  const [preloadedNotes, setPreloadedNotes] = useState<any[]>([])
  const [preloadedParticipants, setPreloadedParticipants] = useState<any[]>([])

  // Form data for general tab
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    privacy: 'private' as 'public' | 'unlisted' | 'private',
    status: 'planning' as 'planning' | 'active' | 'completed' | 'archived',
    type: 'shared' as 'live' | 'shared' | 'personal',
    tags: '',
    startDate: '',
    endDate: '',
    coverImage: '',
    coverImageFileId: '',
    userId: '',
    createdBy: '',
    verified: false,
    isTemplate: false,
    originalTripId: '',
  })

  useEffect(() => {
    loadWaybookAccounts()
    if (!isNewTrip && id) {
      loadTrip(id)
    }
  }, [id, isNewTrip])

  const loadTrip = async (tripId: string) => {
    setLoading(true)
    setError(null)
    try {
      console.log('🔵 Loading trip with id:', tripId)
      const response = await adminApi.getTrip(tripId)
      console.log('✅ Trip API response:', response.data)
      console.log('📦 Response structure:', {
        hasData: !!response.data,
        dataKeys: response.data ? Object.keys(response.data) : [],
        hasNestedData: !!response.data?.data,
        nestedDataKeys: response.data?.data ? Object.keys(response.data.data) : [],
      })

      // API returns { success: true, data: { id, title, events, files, ... } }
      // The trip data is directly in response.data.data (no nested "trip" field)
      const fullData = response.data?.data || response.data
      // Fallback: if server returns data.trip for some reason, use it
      const rawTripData = fullData?.trip || fullData
      console.log('📦 Raw trip data:', rawTripData)
      console.log('📦 Has trip field?', !!fullData?.trip)

      const trips = transformTrips([rawTripData])
      const tripData = trips[0]
      console.log('Transformed trip:', tripData)

      if (!tripData) {
        console.error('Trip not found with id:', tripId)
        showToast('Trip not found. Redirecting to trips list.', { type: 'error' })
        navigate('/trips')
        return
      }

      setTrip(tripData)

      // Store preloaded data from the full endpoint
      // These are now direct properties of rawTripData
      setPreloadedEvents(rawTripData?.events || [])
      setPreloadedFiles(rawTripData?.files || [])
      setPreloadedNotes(rawTripData?.notes || [])
      setPreloadedParticipants(rawTripData?.participants || [])

      // Convert date strings to date-only format for input fields
      // Backend now returns ISO strings like "2023-11-14T22:13:20.000Z"
      const startDateStr = tripData.startDate
        ? new Date(tripData.startDate).toISOString().split('T')[0]
        : ''
      const endDateStr = tripData.endDate
        ? new Date(tripData.endDate).toISOString().split('T')[0]
        : ''

      setFormData({
        title: tripData.title || '',
        description: tripData.description || '',
        privacy: tripData.privacy || 'private',
        status: tripData.status || 'planning',
        type: tripData.type || 'shared',
        tags: '', // No tags field in DB
        startDate: startDateStr,
        endDate: endDateStr,
        coverImage: tripData.coverImageUrl || '',
        coverImageFileId: (tripData as any).coverImageFileId || '',
        userId: '', // Not used for existing trips
        createdBy: tripData.createdBy || '',
        verified: tripData.verified || false,
        isTemplate: tripData.isTemplate || false,
        originalTripId: tripData.originalTripId || '',
      })
      console.log('Form data set:', {
        title: tripData.title,
        description: tripData.description,
        createdBy: tripData.createdBy,
      })
      console.log('Preloaded data:', {
        events: rawTripData?.events?.length || 0,
        files: rawTripData?.files?.length || 0,
        notes: rawTripData?.notes?.length || 0,
        participants: rawTripData?.participants?.length || 0,
      })
    } catch (error: any) {
      console.error('❌ Failed to load trip:', error)
      console.error('🔍 Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL,
          fullURL: error.config?.baseURL + error.config?.url,
        }
      })
      
      // Provide more specific error messages
      let errorMessage = 'Failed to load trip. Please try again.'
      
      if (error.message?.includes('not found') || error.response?.status === 404) {
        errorMessage = `Trip "${tripId}" not found. It may have been deleted or doesn't exist.`
      } else if (error.response?.status === 500) {
        const serverError = error.response?.data?.error || 'Unknown server error'
        errorMessage = `Server error: ${serverError}\n\nThis trip (${tripId}) may not exist or there's a database issue.`
        console.error('💥 Server returned 500 error:', serverError)
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        errorMessage = 'You don\'t have permission to view this trip.'
      }
      
      setError(errorMessage)
      // Show alert for now, but error state is also set for UI display
      showToast(errorMessage, { type: 'error' })
      // Don't navigate away on 500 errors during development for debugging
      if (error.response?.status !== 500) {
        navigate('/trips')
      }
    } finally {
      setLoading(false)
    }
  }

  const loadWaybookAccounts = async () => {
    try {
      const response = await adminApi.getWaybookAccounts()
      let rawData = []
      if (!response.data) {
        rawData = []
      } else if (Array.isArray(response.data)) {
        rawData = response.data
      } else if (typeof response.data === 'object') {
        rawData = response.data?.data || response.data?.items || response.data?.accounts || []
      }
      const data = transformWaybookAccounts(Array.isArray(rawData) ? rawData : [])
      setWaybookAccounts(data)

      console.log('📋 Loaded waybook accounts:', {
        count: data.length,
        accounts: data,
        isNewTrip,
      })

      // Set default user if creating new trip
      if (isNewTrip && data.length > 0) {
        console.log('✅ Setting default userId:', data[0].userId)
        setFormData(prev => ({ ...prev, userId: data[0].userId }))
      }
    } catch (error) {
      console.error('Failed to load waybook accounts:', error)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      // Convert date strings to Unix timestamps in milliseconds (backend uses milliseconds)
      const startTimestamp = formData.startDate
        ? new Date(formData.startDate).getTime()
        : undefined
      const endTimestamp = formData.endDate
        ? new Date(formData.endDate).getTime()
        : undefined

      const dataToSave: any = {
        title: formData.title,
        description: formData.description,
        privacy: formData.privacy,
        status: formData.status,
      }

      // Only add optional fields if they have values
      if (formData.type) dataToSave.type = formData.type
      if (startTimestamp) dataToSave.start_date = startTimestamp
      if (endTimestamp) dataToSave.end_date = endTimestamp
      if (formData.coverImage) dataToSave.cover_image_url = formData.coverImage
      if (formData.coverImageFileId) dataToSave.cover_image_file_id = formData.coverImageFileId
      if (formData.originalTripId) dataToSave.original_trip_id = formData.originalTripId
      if (formData.verified) dataToSave.verified = formData.verified
      if (formData.isTemplate) dataToSave.is_template = formData.isTemplate

      console.log('💾 Saving trip:', {
        isNewTrip,
        userId: formData.userId,
        dataToSave,
      })

      if (isNewTrip) {
        if (!formData.userId) {
          showToast('Please select a user to publish as.', { type: 'error' })
          return
        }
        const response = await adminApi.createTrip(dataToSave, formData.userId)
        // API returns: { success: true, data: { trip_id: "trip_xxx", ... } }
        const newId = response.data?.data?.trip_id || response.data?.trip_id || response.data?.data?.id || response.data?.id
        if (newId) {
          console.log('✅ Trip created successfully, redirecting to:', newId)
          navigate(`/trips/${newId}`, { replace: true })
        } else {
          console.error('❌ No trip_id in response:', response.data)
          showToast('Trip created but failed to get trip ID.', { type: 'error' })
        }
      } else if (id) {
        await adminApi.updateTrip(id, dataToSave)
        await loadTrip(id)
      }
    } catch (error) {
      console.error('Failed to save trip:', error)
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any
        console.error('Error details:', {
          status: axiosError.response?.status,
          statusText: axiosError.response?.statusText,
          data: axiosError.response?.data,
          headers: axiosError.response?.headers,
        })
      }
      showToast('Failed to save trip. Please try again.', { type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const tabs = [
    { id: 'general', name: 'General', icon: FileText },
    { id: 'events', name: 'Events', icon: Calendar },
    { id: 'posts', name: 'Posts', icon: Image },
    { id: 'files', name: 'Files', icon: FileText },
    { id: 'location', name: 'Locations', icon: MapPin },
    { id: 'notes', name: 'Notes', icon: MessageSquare },
    { id: 'participants', name: 'Participants', icon: Users },
  ] as const

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading trip...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/trips" className="text-gray-400 hover:text-gray-600">
            <ArrowLeft className="h-6 w-6" />
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Error Loading Trip</h1>
        </div>
        <div className="card p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="text-red-800 font-semibold mb-2">Failed to load trip</h3>
            <p className="text-red-600 whitespace-pre-line">{error}</p>
            <div className="mt-4">
              <button
                onClick={() => navigate('/trips')}
                className="btn-primary"
              >
                Back to Trips List
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/trips" className="text-gray-400 hover:text-gray-600">
            <ArrowLeft className="h-6 w-6" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isNewTrip ? 'Create New Trip' : 'Edit Trip'}
            </h1>
            <p className="text-gray-600 mt-1">
              {isNewTrip ? 'Fill in the details to create a new trip' : `Editing: ${trip?.title || ''}`}
            </p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !formData.title || (isNewTrip && !formData.userId)}
          className="btn-primary flex items-center gap-2"
        >
          <Save className="h-4 w-4" />
          {saving ? 'Saving...' : 'Save Trip'}
        </button>
      </div>

      {/* Tabs Navigation */}
      <div className="card p-0">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as TabType)}
                  className={`
                    flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm whitespace-nowrap
                    transition-colors
                    ${activeTab === tab.id
                      ? 'text-black'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                  style={activeTab === tab.id ? { borderColor: '#97DF06', color: '#000000' } : undefined}
                  disabled={isNewTrip && tab.id !== 'general'}
                >
                  <Icon className="h-4 w-4" />
                  {tab.name}
                </button>
              )
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'general' && (
          <GeneralTab
            formData={formData}
            setFormData={setFormData}
            waybookAccounts={waybookAccounts}
            isNewTrip={isNewTrip}
            tripId={!isNewTrip && id ? id : undefined}
          />
          )}

          {activeTab === 'events' && !isNewTrip && id && (
            <EventsTab tripId={id} preloadedData={preloadedEvents} userId={formData.createdBy || formData.userId} />
          )}

          {activeTab === 'posts' && !isNewTrip && id && (
            <PostsTab tripId={id} />
          )}

          {activeTab === 'files' && !isNewTrip && id && (
            <FilesTab tripId={id} preloadedData={preloadedFiles} userId={formData.createdBy || formData.userId} />
          )}

          {activeTab === 'location' && (
            <LocationTab
              tripId={id}
              isNewTrip={isNewTrip}
            />
          )}

          {activeTab === 'notes' && !isNewTrip && id && (
            <NotesTab tripId={id} preloadedData={preloadedNotes} />
          )}

          {activeTab === 'participants' && !isNewTrip && id && (
            <ParticipantsTab tripId={id} preloadedData={preloadedParticipants} />
          )}

          {isNewTrip && activeTab !== 'general' && (
            <div className="text-center py-12 text-gray-500">
              Please save the trip first to access this section
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
